<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useSurveys } from "../../Composables/useSurveys.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_SurveyList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: surveys,
    links,
    meta,
    loading,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    loadData,
} = useDataManager({
    baseUrl: '/api/surveys',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const columns = ref([
    { label: '#ID', field: 'id', slot: 'id', class: 'col-1' },
    { label: 'Titre', field: 'title', class: 'col-5' },
    { label: 'Public', slot: 'is_public', class: 'col-1 text-center' },
    { label: 'Date fin', slot: 'end_date', class: 'col-1' },
    { label: 'Échéance', slot: 'dead_line', class: 'col-1 text-center' },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Lien', slot: 'link', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
    }

    loadData(url.value)
})

watch(
    [url, sortingField, sortingFieldsOrder],
    ([newUrl, newSortingField, newSortingOrder]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteSurvey,
} = useSurveys({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="row">
                        <div class="col-md-auto ms-auto" v-if="authUser.can && authUser.can.create_survey">
                            <router-link :to="{ name:'surveys.create' }" class="btn btn-secondary">
                                <i class="ri-add-fill align-bottom"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="surveys"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Public -->
                        <template #is_public="{ item }">
                            <span class="badge fs-11 bg-info-subtle text-info" v-if="item.data.is_public">Public</span>
                            <span class="badge fs-11 bg-secondary-subtle text-secondary" v-else>Personnalisé</span>
                        </template>

                        <!-- Date fin -->
                        <template #end_date="{ item }">
                            {{ item.data.end_date_fr }}
                        </template>

                        <!-- Échéance -->
                        <template #dead_line="{ item }">
                            <span class="badge fs-11 bg-secondary-subtle text-secondary" v-if="item.data.dead_line === 0">Aujourd'hui</span>
                            <span class="badge fs-11 bg-dark-subtle text-dark" v-if="item.data.dead_line < 0">Passée</span>
                            <span class="badge fs-11 bg-success-subtle text-success" v-if="item.data.dead_line > 0">{{ item.data.dead_line }} jours</span>
                        </template>

                        <!-- Date création -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Lien -->
                        <template #link="{ item }">
                            <a :href="item.data.google_form_link" target="_blank" class="btn btn-sm btn-secondary btn-label waves-effect waves-light" :class="item.data.dead_line < 0 ? 'disabled' : ''">
                                <div class="d-flex">
                                    <div class="flex-shrink-0">
                                        <i class="ri-external-link-fill label-icon align-middle fs-16 me-2"></i>
                                    </div>
                                    <div class="flex-grow-1">
                                        Accéder
                                    </div>
                                </div>
                            </a>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'surveys.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le formulaire" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer le formulaire" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer le formulaire ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteSurvey(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer un formulaire. Continuer ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
